import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { PolicyRow, SearchResultItem } from './types';
import {
  capitalizeWords,
  getFormattedDateRange,
  parseEffectiveDateSort,
} from './utils';

interface SearchResultProps {
  lob?: string;
  orgId: string;
  searchResult: SearchResultItem[] | null;
  service_date?: string;
  showLOB?: boolean;
}

const pageSizeOptions = [10, 15, 30, 50];

const SearchResult: React.FC<SearchResultProps> = ({
  lob,
  orgId,
  searchResult,
  service_date,
  showLOB,
}) => {
  const columns: GridColDef<PolicyRow>[] = [
    { field: 'id', flex: 1, headerName: 'ID', width: 90 },
    {
      field: 'policyID',
      flex: 1,
      headerName: 'Policy #',
      width: 90,
    },
    {
      editable: false,
      field: 'policyName',
      flex: 1,
      headerName: 'Policy Name',
    },
    {
      editable: false,
      field: 'effectiveDates',
      flex: 1,
      headerName: 'Effective Dates',
      sortComparator: (_v1, _v2, p1, p2) => {
        const a = p1.api.getRow(p1.id)?.effectiveDateRaw;
        const b = p2.api.getRow(p2.id)?.effectiveDateRaw;
        return new Date(a || '').getTime() - new Date(b || '').getTime();
      },
    },
    {
      editable: false,
      field: 'versionNumber',
      flex: 1,
      headerName: 'Version',
      renderCell: (params) => {
        let urlParams = '';

        if (lob) {
          urlParams = '?lob=' + params.row.lob;
        }

        if (service_date) {
          urlParams = urlParams
            ? urlParams + '&service_date=' + service_date
            : '?service_date=' + service_date;
        }

        return (
          <Link
            to={`/policy/${orgId}/${params.row.policyID}/${params.row.versionID}${urlParams}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.value}
          </Link>
        );
      },
    },
    {
      editable: false,
      field: 'status',
      flex: 1,
      headerName: 'Status',
      renderCell: (params) => {
        const upper = params.value?.toUpperCase();
        if (upper === 'REVIEW') return 'Draft';
        if (upper === 'PUBLISHED') return 'Final';
        return params.value;
      },
    },
    {
      editable: false,
      field: 'lob',
      flex: 1,
      headerName: 'Line of Business',
    },
  ];

  const [policyListing, setPolicyListing] = React.useState<PolicyRow[]>([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const listing: PolicyRow[] = searchResult
      ? searchResult
          .map((item, index) => {
            return {
              effectiveDateRaw: item?.effectivedate ?? null,
              effectiveDates: getFormattedDateRange(
                item?.effectivedate,
                item?.inactivedate ?? ''
              ),
              effectiveDateSort:
                parseEffectiveDateSort(item?.effectivedate) ?? new Date(),
              id: index,
              lob: item?.lob,
              policyID: item?.policy_shortname,
              policyName: item?.policy,
              status: capitalizeWords(item?.workflow),
              versionID: item?.version_id,
              versionNumber: item?.version,
            };
          })
          .sort((a, b) => {
            if (a.policyID < b.policyID) return -1;
            if (a.policyID > b.policyID) return 1;
            return 0;
          })
      : [];

    setPolicyListing(listing);
  }, [searchResult]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '30px',
        width: '100%',
      }}
    >
      <DataGrid
        rows={policyListing}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
              lob: showLOB ? true : false,
            },
          },
          pagination: {
            paginationModel: {
              pageSize: paginationModel.pageSize,
            },
          },
        }}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
        }}
        pageSizeOptions={pageSizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        checkboxSelection={false}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'No Results Found!' }}
      />
    </Box>
  );
};

export default SearchResult;
